/**
 * useCreateCleaningTask — calls create-cleaning-task Edge Function.
 *
 * Maps the structured RPC error codes onto AppError.userMessage so the
 * UI can render an inline blocking message at the right field. Same
 * pattern as useSubmitTask / useToggleChecklistItem.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export type CycleType   = 'turnover' | 'touchup' | 'deep_clean' | 'camp_turn' | 'maintenance_followup'
export type ServiceTier = 'touch_up' | 'normal' | 'heavy' | 'partial' | 'special' | 'exceeds_scope_pending_owner_approval'

export interface CreateTaskVars {
  roomId:                     string
  cycleType:                  CycleType
  serviceTier:                ServiceTier
  assignedToUserId?:          string | null
  estimatedMinutes?:          number | null
  isPartialUnit?:             boolean
  kitchenExcluded?:           boolean
  partialScopeDescription?:   string | null
}

export interface CreateTaskResult {
  task_id:       string
  room_id:       string
  items_seeded:  number
}

export function useCreateCleaningTask(): UseMutationResult<CreateTaskResult, AppError, CreateTaskVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<CreateTaskResult, AppError, CreateTaskVars>({
    mutationFn: async (vars): Promise<CreateTaskResult> => {
      const { data, error } = await supabase.functions.invoke<CreateTaskResult>('create-cleaning-task', {
        body: {
          room_id:                     vars.roomId,
          cycle_type:                  vars.cycleType,
          service_tier:                vars.serviceTier,
          assigned_to_user_id:         vars.assignedToUserId ?? null,
          estimated_minutes:           vars.estimatedMinutes ?? null,
          is_partial_unit:             vars.isPartialUnit ?? false,
          kitchen_excluded:            vars.kitchenExcluded ?? false,
          partial_scope_description:   vars.partialScopeDescription ?? null,
        },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'CREATE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'The server returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: () => {
      // New task lands in everyone's lists. Invalidate the surfaces
      // that read cleaning_tasks so the page they navigate to next
      // shows fresh data.
      void queryClient.invalidateQueries({ queryKey: ['my-tasks']             })
      void queryClient.invalidateQueries({ queryKey: ['pending-inspections']  })
      void queryClient.invalidateQueries({ queryKey: ['room-board']           })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  // ROOM_HAS_OPEN_TASK arrives as "ROOM_HAS_OPEN_TASK:<task_id>" from
  // the RPC. mapRpcError splits on the colon so code here is the bare
  // value, but the message we surface stays generic.
  switch (code) {
    case 'ROOM_HAS_OPEN_TASK':     return 'A task is already active on this room. Finish or reject it before creating another.'
    case 'ROOM_NOT_FOUND':         return 'That room could not be found.'
    case 'ASSIGNEE_NOT_FOUND':     return 'The assigned user was not found.'
    case 'FORBIDDEN_ROLE':         return 'Only supervisors and above can create tasks.'
    case 'FORBIDDEN_ORG':          return 'You can only create tasks in your own organization.'
    case 'FORBIDDEN_ASSIGNEE':     return 'You can only assign tasks to people in your organization.'
    case 'PARTIAL_SCOPE_TOO_LONG': return 'Scope description must be 500 characters or fewer.'
    case 'INVALID_PARTIAL_FLAGS':  return 'Partial-unit details require the partial-unit checkbox to be checked.'
    case 'INVALID_CYCLE_TYPE':     return 'Pick a valid cycle type.'
    case 'INVALID_SERVICE_TIER':   return 'Pick a valid service tier.'
    case 'INVALID_ASSIGNEE':       return 'The assigned user ID is not valid.'
    case 'INVALID_ESTIMATE':       return 'Estimated minutes must be a positive whole number.'
    case 'RATE_LIMITED':           return 'Too many tasks created in a row. Pause for a moment.'
    default:                       return detail || 'Could not create the task.'
  }
}
